import { useState } from "react";
import { useDispatch } from "react-redux";

import * as Api from "@/api";
import { showSnackbar } from "@/store/reducers/app";
import { formatToMac } from "@/utils/utility";
import Valkyrie, { viArrowsRotateRight, viClockRotateLeft, viPower, viXmark } from "@sippy-platform/valkyrie";

import { Box, Button, Card, CardContent, Dialog, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";

function DeviceCard({ specs, provisioning }) {
  const dispatch = useDispatch();

  const [reboot, setReboot] = useState(null);
  const [reset, setReset] = useState(null);
  const [sync, setSync] = useState(null);

  const syncDevice = async () => {
    setSync(provisioning.id);
    dispatch(showSnackbar("Device is going to sync."));

    await Promise.resolve(Api.actionProvisioningSync(provisioning))
      .then(() => setSync(null))
      .catch(() => setSync(null));
  };

  const rebootDevice = async () => {
    setReboot(provisioning.id);
    dispatch(showSnackbar("Device is going to reboot."));

    await Promise.resolve(Api.actionProvisioningReboot(provisioning))
      .then(() => setReboot(null))
      .catch(() => setReboot(null));
  };

  const resetDevice = async () => {
    setReset(provisioning.id);
    setResetDialog(false);
    dispatch(showSnackbar("Device is going to reset."));

    await Promise.resolve(Api.actionProvisioningReset(provisioning))
      .then(() => setReset(null))
      .catch(() => setReset(null));
  };

  const [resetdialog, setResetDialog] = useState(false);

  return (
    <>
      <Card sx={{ containerType: "inline-size" }}>
        <CardContent>
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,

              ["@container (min-width: 524px)"]: {
                justifyContent: "space-between",
                flexDirection: "row",
                gap: 3,
              },
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 3,
              }}
            >
              <Box
                component="img"
                src={specs?.imageUrl ? specs.imageUrl : "/unknown-device.png"}
                alt={specs?.brand ? `${specs.brand} ${specs.type}` : "Unsupported device"}
                sx={{
                  maxWidth: 120,
                  ["@container (min-width: 360px)"]: {
                    maxWidth: 144,
                  },
                  ["@container (min-width: 524px)"]: {
                    maxWidth: 168,
                  },
                  width: "auto",
                  borderRadius: 2,
                }}
              />
              <Stack>
                {specs ? (
                  <Typography variant="h4" sx={{ mb: 1, fontSize: { xs: 20, xl: 28 } }}>
                    {specs?.brand ? `${specs.brand} ${specs.type}` : "Loading..."}
                  </Typography>
                ) : (
                  <Typography variant="h4" sx={{ mb: 1, fontSize: { xs: 20, xl: 28 } }}>
                    Unsupported device
                  </Typography>
                )}
                {provisioning.mac && (
                  <Typography variant="body1">
                    <Typography component="span" sx={{ display: "inline", color: "text.secondary" }}>
                      MAC:
                    </Typography>
                    &nbsp;
                    <Typography component="span" sx={{ display: "inline", userSelect: "all" }}>
                      {formatToMac(provisioning.mac)}
                    </Typography>
                  </Typography>
                )}
                {provisioning.firmware && (
                  <Typography variant="body1">
                    <Typography component="span" sx={{ display: "inline", color: "text.secondary" }}>
                      Firmware:
                    </Typography>
                    &nbsp;
                    <Typography component="span" sx={{ display: "inline", userSelect: "all" }}>
                      {provisioning.firmware}
                    </Typography>
                  </Typography>
                )}
                {provisioning.serialNumber && (
                  <Typography variant="body1">
                    <Typography component="span" sx={{ display: "inline", color: "text.secondary" }}>
                      Serial number:
                    </Typography>
                    &nbsp;
                    <Typography component="span" sx={{ display: "inline", userSelect: "all" }}>
                      {provisioning.serialNumber}
                    </Typography>
                  </Typography>
                )}
                {provisioning.description && (
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {provisioning.description}
                  </Typography>
                )}
              </Stack>
            </Stack>
            {specs && (
              <Stack
                direction="row"
                sx={{
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  gap: 0.5,
                }}
              >
                <Button
                  loading={reset === provisioning.id}
                  loadingPosition="start"
                  disabled={
                    (!provisioning.sippeer && provisioning.flexDesk === 0) || reset !== null || !specs?.actionURI
                  }
                  onClick={() => setResetDialog(true)}
                  startIcon={<Valkyrie icon={viClockRotateLeft} />}
                >
                  Reset
                </Button>
                <Button
                  loading={reboot === provisioning.id}
                  loadingPosition="start"
                  disabled={(!provisioning.sippeer && provisioning.flexDesk === 0) || reboot !== null}
                  onClick={() => rebootDevice()}
                  startIcon={<Valkyrie icon={viPower} spin={reboot} />}
                >
                  Reboot
                </Button>
                <Button
                  loading={sync === provisioning.id}
                  loadingPosition="start"
                  disabled={(!provisioning.sippeer && provisioning.flexDesk === 0) || sync !== null}
                  onClick={() => syncDevice()}
                  startIcon={<Valkyrie icon={viArrowsRotateRight} spin={sync} />}
                >
                  Sync
                </Button>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
      <Dialog open={resetdialog} onClose={() => setResetDialog(false)} maxWidth="xs">
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <DialogTitle sx={{ p: 0 }}>Device Factory Reset</DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Typography>
              You are about to reset this devices to its factory settings. This may disconnect your device from Sippy.
              Are you sure you want to continue?
            </Typography>
          </DialogContent>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Button onClick={() => setResetDialog(false)} variant="outlined">
              Cancel
            </Button>
            <Button onClick={resetDevice} variant="contained" color="error" startIcon={<Valkyrie icon={viXmark} />}>
              Factory reset device
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}

DeviceCard.propTypes = {
  provisioning: PropTypes.object,
  specs: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
};

export default DeviceCard;
